import React, { forwardRef, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import Button from "src/components/core/Button";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

/**
 * @typedef {Object} Column
 * @property {string} field
 * @property {string} label
 * @property {"left" | "right" | "center"} [align]
 * @property {number} [width]
 * @property {string[]} userRoles
 * @property {boolean} [sortable]
 * @property {(params: { row: any }) => React.ReactNode} renderCell
 * @property {Column[]} [subTableColumns]
 * @property {string} [subTableDataField]
 */

/**
 * @param {{
 *  columns: Column[],
 *  rows: any[],
 *  components: { NoRowsOverlay: React.ComponentType, NoResultsOverlay?: React.ComponentType },
 *  sortBy: string,
 *  sortOrder: "asc" | "desc",
 *  onSort: (field: string) => void,
 *  isLoading: boolean,
 *  collapsible?: boolean,
 *  getToggleButtonText?: (isOpen: boolean, count: number) => string
 * }} props
 */
const CollapsibleTable = forwardRef(
  (
    {
      rows,
      columns,
      subTableColumns = [],
      subTableDataField = "",
      components,
      sortBy,
      sortOrder,
      onSort,
      isLoading,
      collapsible = true,
      getToggleButtonText,
      isAlwaysExpanded = false,
    },
    ref,
  ) => {
    const { NoRowsOverlay } = components;

    return (
      <TableContainer ref={ref} component={Paper} sx={{ height: 600 }}>
        <Table
          aria-label="collapsible table"
          stickyHeader
          sx={{
            tableLayout: "fixed",
            ...((rows.length === 0 || isLoading) && { height: "100%" }),
            position: "relative",
          }}
        >
          <TableHead
            sx={{
              "& .MuiTableCell-head": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <TableRow>
              {columns.map((column) => {
                return (
                  <TableCell
                    key={column.field}
                    align={column.align}
                    style={{ width: column?.width }}
                  >
                    {column.renderHeader?.()}
                    {column.sortable ? (
                      <TableSortLabel
                        active={sortBy === column.field}
                        direction={sortBy === column.field ? sortOrder : "asc"}
                        onClick={() => onSort(column.field)}
                        sx={{
                          color: "rgba(0, 0, 0, 0.87)",
                          fontWeight: 500,
                          fontSize: "0.875rem",
                        }}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          {isLoading ? (
            <Box sx={{ position: "absolute", top: "50%", left: "50%" }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableBody>
              {rows.length > 0 ? (
                rows.map((row, index) => (
                  <Row
                    key={index}
                    row={row}
                    columns={columns}
                    collapsible={collapsible}
                    subTableColumns={subTableColumns}
                    subTableDataField={subTableDataField}
                    getToggleButtonText={getToggleButtonText}
                    isAlwaysExpanded={isAlwaysExpanded}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <NoRowsOverlay />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
    );
  },
);

CollapsibleTable.displayName = "CollapsibleTable";
export default CollapsibleTable;

/**
 * @param {{
 *  row: any,
 *  columns: Column[],
 *  subTableColumns?: Column[],
 *  subTableDataField?: string,
 *  collapsible?: boolean,
 *  getToggleButtonText?: (isOpen: boolean, count: number) => string,
 * }} props
 */
const Row = ({
  row,
  columns,
  subTableColumns = [],
  subTableDataField = "",
  collapsible = true,
  getToggleButtonText,
  isAlwaysExpanded,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const subRows = row[subTableDataField] ?? [];
  const showCollapse = collapsible && subRows.length > 1;

  const toggleText =
    getToggleButtonText?.(isOpen, subRows.length) ??
    (isOpen ? "ดูตัวเลือกน้อยลง" : `ดู ${subRows.length} ตัวเลือกเพิ่มเติม`);

  useEffect(() => {
    if (isAlwaysExpanded) {
      setIsOpen(true);
    }
  }, [isAlwaysExpanded]);

  return (
    <>
      <TableRow
        sx={{
          ...(subTableColumns.length > 1 && {
            "& .MuiTableCell-root": { borderBottom: 0 },
          }),
        }}
      >
        {columns.map((column) => (
          <TableCell
            key={column.field}
            align={column.align}
            style={{ width: column?.width }}
          >
            {column.renderCell({ row })}
          </TableCell>
        ))}
      </TableRow>

      {showCollapse && (
        <TableRow>
          <TableCell colSpan={subTableColumns.length} padding="none">
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <Table style={{ marginBottom: 16 }}>
                <TableBody>
                  {subRows.map((subRow, index) => (
                    <TableRow
                      key={index}
                      sx={{ "& .MuiTableCell-root": { borderBottom: 0 } }}
                    >
                      {subTableColumns.map((subCol) => (
                        <TableCell
                          key={subCol.field}
                          align={subCol.align}
                          style={{ width: subCol?.width }}
                        >
                          {subCol.renderCell({ row: subRow })}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Collapse>
            <Button
              variant="text"
              endIcon={
                isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
              }
              onClick={() => setIsOpen(!isOpen)}
              style={{ marginLeft: 16, marginBottom: 16 }}
            >
              {toggleText}
            </Button>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
